import React, { useEffect, useState, useRef, useContext } from "react";
import { fetchNotificationsApi } from "../api/services/notificationService";
import Sidebar from "../components/Sidebar";
import Pagination from "../components/Pagination";
import { NotificationAlertContext } from "../app/NotificationAlertContext";

const FILTERS = [
  { label: "Today", value: "today" },
  { label: "Last 7 Days", value: "7days" },
  { label: "Last Month", value: "month" },
  { label: "Custom Date", value: "custom" },
];

function isSameDay(date1, date2) {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

function isWithinLastNDays(date, n) {
  const now = new Date();
  const past = new Date();
  past.setDate(now.getDate() - n + 1);
  return date >= past && date <= now;
}

function isWithinMonth(date, month, year) {
  return date.getMonth() === month && date.getFullYear() === year;
}

function parseOrderItems(itemsStr) {
  const items = [];
  let depth = 0;
  let currentItem = "";
  let inString = false;
  let stringChar = "";

  for (let i = 0; i < itemsStr.length; i++) {
    const char = itemsStr[i];
    if (inString) {
      currentItem += char;
      if (char === stringChar) inString = false;
    } else if (char === "'" || char === '"') {
      inString = true;
      stringChar = char;
      currentItem += char;
    } else if (char === "{") {
      depth++;
      currentItem += char;
    } else if (char === "}") {
      depth--;
      currentItem += char;
      if (depth === 0) {
        const itemStr = currentItem.trim();
        const nameMatch = itemStr.match(/item:\s*'([^']*)'/);
        const quantityMatch = itemStr.match(/quantity:\s*(\d+)/);
        const priceMatch = itemStr.match(/price:\s*([\d.]+)/);
        const optionsAreaMatch = itemStr.match(/options_to_add:\s*\[([\s\S]*?)\]/);
        const options = [];
        if (optionsAreaMatch && optionsAreaMatch[1].trim()) {
          [...optionsAreaMatch[1].matchAll(/title:\s*'([^']*)'/g)].forEach(
            (m) => options.push(m[1])
          );
        }
        items.push({
          name: nameMatch ? nameMatch[1] : "Item",
          quantity: quantityMatch ? parseInt(quantityMatch[1]) : 1,
          price: priceMatch ? parseFloat(priceMatch[1]) : 0,
          options,
        });
        currentItem = "";
      }
    } else if (depth > 0) {
      currentItem += char;
    }
  }
  return items;
}

function formatMessage(msg) {
  if (!msg) return "";
  if (typeof msg === "object") return JSON.stringify(msg, null, 2);

  const deliveryMatch = msg.match(
    /^(.*?has been delivered carrying\s)([\s\S]+?)(\s*has been delivered successfully)$/
  );
  if (deliveryMatch) {
    const header = deliveryMatch[1].replace(" carrying", "");
    const items = parseOrderItems(deliveryMatch[2]);
    return (
      <div className="flex flex-col gap-1">
        <span>{header}</span>
        <ul className="flex flex-col gap-0.5 pl-1">
          {items.map((item, i) => (
            <li key={i} className="text-sm">
              <span className="font-semibold">{item.name}</span>
              {` × ${item.quantity} — £${item.price.toFixed(2)}`}
              {item.options.length > 0 && (
                <span className="text-gray-500 text-xs">
                  {" "}({item.options.join(", ")})
                </span>
              )}
            </li>
          ))}
        </ul>
        <span className="text-green-600 text-xs font-medium">Delivered successfully</span>
      </div>
    );
  }

  return msg;
}

const PAGE_SIZE = 10;

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState("today");
  const [customDate, setCustomDate] = useState("");
  const [customMonth, setCustomMonth] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const { setNotificationAlert, newNotification } = useContext(
    NotificationAlertContext
  );
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Listen to new notifications from context
  useEffect(() => {
    if (newNotification) {
      setNotifications((prev) => [newNotification, ...prev]);
    }
  }, [newNotification]);

  useEffect(() => {
    fetchNotificationsApi().then((data) => {
      setNotifications(Array.isArray(data) ? data : []);
    });
    setNotificationAlert(false);
  }, [setNotificationAlert]);

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  // Filtering logic
  const filteredNotifications = notifications.filter((n) => {
    if (!n.createdAt) return false;
    const notifDate = new Date(n.createdAt);
    const now = new Date();
    if (filter === "today") {
      return isSameDay(notifDate, now);
    } else if (filter === "7days") {
      return isWithinLastNDays(notifDate, 7);
    } else if (filter === "month") {
      return (
        notifDate.getMonth() === now.getMonth() &&
        notifDate.getFullYear() === now.getFullYear()
      );
    } else if (filter === "custom" && customDate) {
      const selected = new Date(customDate);
      return isSameDay(notifDate, selected);
    } else if (filter === "custom" && customMonth) {
      const [year, month] = customMonth.split("-").map(Number);
      return isWithinMonth(notifDate, month - 1, year);
    }
    return true;
  });

  // Pagination logic
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  const currentTableData = filteredNotifications.slice(startIndex, endIndex);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, customDate, customMonth]);

  const isEmpty = currentTableData.length === 0;

  return (
    <div className="lg:flex hidden items-start">
      <Sidebar />
      <div className="ml-[14.5vw] w-full h-screen overflow-y-auto px-10 py-8 text-secondary">
        <div className="flex flex-col md:flex-row md:items-end gap-4 mb-6">
          <div>
            <label className="block font-semibold mb-1">Filter</label>
            <div className="relative w-[180px]" ref={dropdownRef}>
              <button
                type="button"
                className={`flex items-center justify-between w-full border-2 rounded-t-lg rounded-b-lg px-4 py-2 font-medium shadow-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-[#F75E00] ${
                  dropdownOpen || filter ? "border-[#F75E00]" : "border-gray-300"
                } bg-white text-secondary cursor-pointer hover:border-[#F75E00]`}
                onClick={() => setDropdownOpen((v) => !v)}
                tabIndex={0}
                aria-haspopup="listbox"
                aria-expanded={dropdownOpen}
              >
                <span className="truncate">{FILTERS.find((f) => f.value === filter)?.label}</span>
                <span className={`ml-2 transition-transform ${dropdownOpen ? "rotate-180" : ""} text-[#F75E00]`}>
                  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </button>
              {dropdownOpen && (
                <ul
                  className="absolute left-0 mt-1 w-full bg-white border-2 border-[#F75E00] rounded-b-lg rounded-t-none shadow-lg z-10 overflow-hidden animate-fadeIn"
                  tabIndex={-1}
                  role="listbox"
                >
                  {FILTERS.map((f) => (
                    <li
                      key={f.value}
                      className={`px-4 py-2 cursor-pointer transition-colors duration-100 text-secondary font-medium select-none ${
                        filter === f.value
                          ? "bg-[#F75E00] text-white"
                          : "hover:bg-[#F75E00] hover:text-white"
                      }`}
                      onClick={() => {
                        setFilter(f.value);
                        setDropdownOpen(false);
                      }}
                      role="option"
                      aria-selected={filter === f.value}
                    >
                      {f.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          {filter === "custom" && (
            <div className="flex gap-2 items-center">
              <label className="font-semibold">Date:</label>
              <input
                type="date"
                className="border border-primary rounded-md px-2 py-1"
                value={customDate}
                onChange={(e) => {
                  setCustomDate(e.target.value);
                  setCustomMonth("");
                }}
              />
              <span className="text-gray-500">or</span>
              <label className="font-semibold">Month:</label>
              <input
                type="month"
                className="border border-primary rounded-md px-2 py-1"
                value={customMonth}
                onChange={(e) => {
                  setCustomMonth(e.target.value);
                  setCustomDate("");
                }}
              />
            </div>
          )}
        </div>
        <div
          className={`border-2 border-t-4 border-quaternary bg-[#F7F9F8] text-[#413F3F] overflow-x-scroll relative rounded-xl h-full px-3 ${
            isEmpty ? "min-h-[180px] max-h-[220px] flex items-center justify-center" : ""
          }`}
        >
          <div className="flex flex-col">
            <div className="w-full rounded-t-lg">
              <table className="w-full table-auto divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr>
                    <th className="text-start text-[14px] font-bold leading-5 px-4 py-1 h-12 whitespace-nowrap">
                      Subject
                    </th>
                    <th className="text-start text-[14px] font-bold leading-5 px-4 py-1 h-12 whitespace-nowrap">
                      Message
                    </th>
                    <th className="text-start text-[14px] font-bold leading-5 px-4 py-1 h-12 whitespace-nowrap">
                      Date/Time
                    </th>
                  </tr>
                </thead>
                {currentTableData.length > 0 ? (
                  <tbody className="divide-y-[1px] divide-[#8D8D8D]">
                    {currentTableData.map((n, i) => (
                      <tr
                        key={n._id || i}
                        className={`${i % 2 === 0 ? "bg-gray-100" : ""}`}
                      >
                        <td className="px-4 py-4 text-sm font-medium">
                          {n.notificationSubject || n.subject || "Notification"}
                        </td>
                        <td className="px-4 py-4 text-sm font-medium">
                          {formatMessage(n.notificationMessage || n.message)}
                        </td>
                        <td className="px-4 py-4 text-sm font-medium text-primary">
                          {n.createdAt ? new Date(n.createdAt).toLocaleString("en-GB", { timeZone: "Europe/London" }) : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                ) : (
                  <tbody>
                    <tr>
                      <td
                        colSpan={3}
                        className="px-3 py-4 italic text-sm font-medium text-center"
                      >
                        No notifications found — rumor has it, everyone's busy working!
                      </td>
                    </tr>
                  </tbody>
                )}
              </table>
            </div>
          </div>
        </div>
        <div className="flex justify-center mt-4 mb-2">
          <Pagination
            className="pagination-bar"
            currentPage={currentPage}
            totalCount={filteredNotifications.length}
            pageSize={PAGE_SIZE}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>
    </div>
  );
} 